/**
 * 图片参考上传节点 - 用于图生图
 */
import React, { memo, useState, useCallback } from 'react';
import { useReactFlow } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { NodeLayout } from './NodeLayout';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import { useDropzone } from 'react-dropzone';
import ApiService from '../../../api/ApiService';
import { toast } from 'react-toastify';
import ModelSelectionModal from '../../ModelSelectionModal';
import { getModelsByMode, getDefaultModelData } from './modelConfigs';
import './ImageReferenceNode.css';

const apiService = new ApiService();

export const ImageReferenceNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getNodes } = useReactFlow();
  const { readOnly } = useProject();
  const { addNodesWithEdges } = useNodeOperations();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  // 弹窗状态
  const [showModelModal, setShowModelModal] = useState(false);
  const [modalMode, setModalMode] = useState('image'); // 'image' | 'video'

  // 判断是否为本地文件URL（data: 或 blob:）
  const isLocalFileUrl = (url) => {
    if (!url) return false;
    return url.startsWith('data:') || url.startsWith('blob:');
  };

  // 处理图片上传
  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      setUploading(true);
      setUploadProgress(0);
      setError(null);

      try {
        // 1. 先显示本地预览
        const reader = new FileReader();
        reader.onload = () => {
          const previewUrl = reader.result;
          updateNodeData(id, {
            imageUrl: previewUrl,
            fileName: file.name,
            isUploading: true,
          });
        };
        reader.readAsDataURL(file);

        setUploadProgress(30);
        console.log('开始上传图片到R2...', file.name);

        // 2. 上传到 Cloudflare R2
        const uploadResult = await apiService.uploadImageToR2(file);
        
        setUploadProgress(90);
        console.log('上传成功，公开URL:', uploadResult.public_url);

        // 3. 更新节点数据为云端URL
        updateNodeData(id, {
          imageUrl: uploadResult.public_url,
          fileName: file.name,
          fileKey: uploadResult.file_key,
          isUploading: false,
        });

        setUploadProgress(100);
        toast.success('图片上传成功');
      } catch (error) {
        console.error('上传失败:', error);
        const errorMessage = error.message || '上传失败，请重试';
        setError(errorMessage);
        toast.error(errorMessage);
        
        // 清除预览图片
        updateNodeData(id, {
          imageUrl: undefined,
          fileName: undefined,
          fileKey: undefined,
          isUploading: false,
        });
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    },
    [id, updateNodeData]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.gif'],
    },
    maxFiles: 1,
    disabled: readOnly,
  });

  // 清除图片
  const handleClear = () => {
    if (readOnly) return;
    updateNodeData(id, {
      imageUrl: undefined,
      fileName: undefined,
      fileKey: undefined,
      isUploading: false,
    });
    setError(null);
  };

  // 计算新节点位置，避免重叠
  const calculateModelNodePosition = useCallback((sourceNode, existingNodes) => {
    const baseOffset = { x: 600, y: 0 }; // 水平偏移 600px，垂直对齐
    let position = {
      x: sourceNode.position.x + baseOffset.x,
      y: sourceNode.position.y + baseOffset.y,
    };

    // 检测重叠，如果有重叠则向下偏移
    const nodeWidth = 320;   // 节点宽度
    const nodeHeight = 200;  // 节点高度
    const verticalOffset = 150; // 垂直偏移量

    let hasOverlap = true;
    let attempts = 0;
    const maxAttempts = 10;

    while (hasOverlap && attempts < maxAttempts) {
      hasOverlap = existingNodes.some((node) => {
        if (!node.position) return false;

        const dx = Math.abs(node.position.x - position.x);
        const dy = Math.abs(node.position.y - position.y);

        return dx < nodeWidth && dy < nodeHeight;
      });

      if (hasOverlap) {
        position.y += verticalOffset;
        attempts++;
      }
    }

    return position;
  }, []);

  // 处理模型选择
  const handleModelSelect = useCallback(async (modelType) => {
    try {
      // 关闭弹窗
      setShowModelModal(false);

      // 获取当前节点信息
      const sourceNode = getNode(id);
      if (!sourceNode) {
        toast.error('获取源节点失败');
        return;
      }

      // 获取所有现有节点
      const existingNodes = getNodes();

      // 计算新节点位置
      const position = calculateModelNodePosition(sourceNode, existingNodes);

      // 创建新的模型节点
      const newNodeId = nanoid();
      const newNode = {
        id: newNodeId,
        type: modelType,
        position,
        data: getDefaultModelData(modelType),
        selected: true, // 选中新创建的节点
      };

      // 创建连接边
      // 图片参考节点输出图片，所以连接到模型节点的图片输入
      const newEdge = {
        id: nanoid(),
        source: id,
        sourceHandle: 'output', // 图片参考节点的输出
        target: newNodeId,
        targetHandle: 'image', // 目标节点的图片输入
        type: 'default',
      };

      // 添加节点和边
      await addNodesWithEdges([newNode], [newEdge]);

      // 显示成功提示
      const modelNames = {
        gptImage2: 'GPT 2 图像生成',
        nanoBanana2: 'Nano 2 图像生成',
        minimaxH3Video: '海螺 H3 视频生成',
        klingO3ReferenceVideo: '可灵 O3 视频生成',
        geminiOmniFlashVideo: 'Gemini 极速视频生成',
        superSeed2LiteVideo: 'Seedance 2.5 视频生成',
        imengImageGen: '即梦4.0生图',
        minimaxImage: '海螺画图',
        googleBanana: '谷歌nano banana',
        textToImage: '文生图',
        referenceToImage: '参考生图',
        sora2Video: 'SORA2视频',
        veo3Video: 'VEO3视频',
        klingVideo: '可灵2.5',
        hailuo23Video: '海螺2.3视频',
        doubaoVideo: '豆包视频',
      };

      toast.success(`已创建 ${modelNames[modelType] || modelType} 节点`);
    } catch (error) {
      console.error('创建模型节点失败:', error);
      toast.error('创建节点失败，请重试');
    }
  }, [id, getNode, getNodes, calculateModelNodePosition, addNodesWithEdges]);

  // 图片按钮点击
  const handleImageButtonClick = useCallback((e) => {
    e.stopPropagation();
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    setModalMode('image');
    setShowModelModal(true);
  }, [readOnly]);

  // 视频按钮点击
  const handleVideoButtonClick = useCallback((e) => {
    e.stopPropagation();
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    setModalMode('video');
    setShowModelModal(true);
  }, [readOnly]);

  return (
    <>
      <NodeLayout
        id={id}
        type="imageReference"
        title="图片参考"
        showInputHandle={false}
        showOutputHandle={true}
      >
        <div className="ImageReferenceNode-wrapper">
          {/* 右侧悬浮超级按钮组 */}
          <div className="ImageReferenceNode-super-buttons">
            {/* 图片按钮 */}
            <button
              className="ImageReferenceNode-super-button ImageReferenceNode-super-button-image"
              title="添加图片模型"
              disabled={readOnly}
              onClick={handleImageButtonClick}
            >
              <i className="bi bi-image-fill"></i>
              <span className="ImageReferenceNode-super-button-label">图片</span>
            </button>

            {/* 视频按钮 */}
            <button
              className="ImageReferenceNode-super-button ImageReferenceNode-super-button-video"
              title="添加视频模型"
              disabled={readOnly}
              onClick={handleVideoButtonClick}
            >
              <i className="bi bi-camera-video-fill"></i>
              <span className="ImageReferenceNode-super-button-label">视频</span>
            </button>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="ImageReferenceNode-error">
              {error}
            </div>
          )}

          {data.imageUrl ? (
            // 显示已上传的图片
            <div className="ImageReferenceNode-preview">
              <img
                src={data.imageUrl}
                alt={data.fileName || '参考图片'}
                className="ImageReferenceNode-image"
              />
              {!data.isUploading && !readOnly && (
                <button
                  onClick={handleClear}
                  className="ImageReferenceNode-clear"
                  title="删除图片"
                >
                  ×
                </button>
              )}
              <div className="ImageReferenceNode-info">
                {data.isUploading && (
                  <div className="ImageReferenceNode-upload-status">
                    <div className="ImageReferenceNode-loading-small" />
                    <span>上传中...</span>
                  </div>
                )}
                {data.fileName && (
                  <div className="ImageReferenceNode-filename">
                    {data.fileName}
                  </div>
                )}
                {!data.isUploading && isLocalFileUrl(data.imageUrl) && (
                  <div className="ImageReferenceNode-warning">
                    ⚠️ 本地预览（未上传）
                  </div>
                )}
              </div>
            </div>
          ) : (
            // 上传区域
            <div
              {...getRootProps()}
              className={`ImageReferenceNode-dropzone ${isDragActive ? 'ImageReferenceNode-dropzone-active' : ''}`}
            >
              <input {...getInputProps()} />
              <div className="ImageReferenceNode-dropzone-content">
                {uploading ? (
                  <>
                    <div className="ImageReferenceNode-loading" />
                    <div className="ImageReferenceNode-text">
                      上传中 {uploadProgress > 0 ? `${uploadProgress}%` : '...'}
                    </div>
                  </>
                ) : (
                  <>
                    <svg className="ImageReferenceNode-icon" width="32" height="32" viewBox="0 0 32 32" fill="none">
                      <rect x="4" y="8" width="24" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="11" cy="14" r="2" fill="currentColor"/>
                      <path d="M4 20 L10 16 L16 20 L24 13 L28 16" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <div className="ImageReferenceNode-text">
                      {isDragActive ? '放开以上传图片' : '拖拽图片到这里或点击上传'}
                    </div>
                    <div className="ImageReferenceNode-hint">
                      支持 PNG、JPG、WEBP 等格式
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </NodeLayout>

      {/* 模型选择弹窗 */}
      <ModelSelectionModal
        show={showModelModal}
        onHide={() => setShowModelModal(false)}
        mode={modalMode}
        models={getModelsByMode(modalMode)}
        onSelect={handleModelSelect}
      />
    </>
  );
});

ImageReferenceNode.displayName = 'ImageReferenceNode';
